/*
 * The time rules of a staff session as the page sees them (ADR 0008 §4): 8 hours at most from
 * the sign-in (`endsAt`, from auth.state) and 30 minutes without activity (lib/activity.ts). The
 * server decides; the page only warns ahead and, once a limit has passed, asks the server, which
 * closes the session and says so. Pure, for the tests.
 */

/** Before the idle limit, a dialog asks whether the person is still there (WCAG 2.2.1). */
export const IDLE_WARNING_MS = 2 * 60_000
/** Before the 8 hours, a notice says when the session will end. */
export const ENDING_NOTICE_MS = 10 * 60_000

export interface SessionTimes {
  now: number
  /** The last activity the server counted. */
  lastActivity: number
  /** Minutes without activity that close the session (auth.state). */
  idleMinutes: number
  /** The end of the session whatever happens. */
  endsAt: number
}

export interface SessionPhase {
  /** When the session closes if nothing happens before. */
  idleAt: number
  /** The idle limit is near: ask whether the person is still there. */
  idleWarning: boolean
  /** The 8 hours are near: say when the session ends. */
  endingSoon: boolean
  /** A limit has passed: the server is to be asked, and the session will be closed. */
  over: false | 'idle' | 'expired'
}

export function sessionPhase({
  now,
  lastActivity,
  idleMinutes,
  endsAt,
}: SessionTimes): SessionPhase {
  const idleAt = lastActivity + idleMinutes * 60_000
  const over = now >= endsAt ? 'expired' : now >= idleAt ? 'idle' : false
  return {
    idleAt,
    idleWarning: !over && now >= idleAt - IDLE_WARNING_MS,
    endingSoon: !over && now >= endsAt - ENDING_NOTICE_MS,
    over,
  }
}
